import React from 'react';
import { HelpCircle } from 'lucide-react';

/**
 * RiskMeter Component
 * Displays a visual risk meter with green/yellow/red zones
 * 
 * @param {number} value - Risk value (0-100)
 * @param {string} label - Label for the meter
 * @param {string} tooltip - Tooltip text explaining the risk
 */
const RiskMeter = ({ value = 50, label = 'Risk Level', tooltip = '' }) => {
  // Determine risk level and color
  const getRiskLevel = (val) => {
    if (val < 30) return { level: 'Low', color: 'green' };
    if (val < 70) return { level: 'Medium', color: 'yellow' };
    return { level: 'High', color: 'red' };
  };

  const risk = getRiskLevel(value);
  const percentage = Math.min(100, Math.max(0, value));

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </span>
        {tooltip && (
          <div className="group relative">
            <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
            <div className="absolute right-0 top-6 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
              {tooltip}
            </div>
          </div>
        )}
      </div>
      
      {/* Linear meter */}
      <div className="relative h-6 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        {/* Background zones */}
        <div className="absolute inset-0 flex">
          <div className="flex-1 bg-green-500"></div>
          <div className="flex-1 bg-yellow-500"></div>
          <div className="flex-1 bg-red-500"></div>
        </div>
        
        {/* Value indicator */}
        <div
          className="absolute left-0 top-0 h-full bg-slate-700 transition-all duration-300 dark:bg-surface-container-lowest"
          style={{ width: `${percentage}%` }}
        >
          <div className="absolute right-0 top-0 w-1 h-full bg-white"></div>
        </div>
      </div>
      
      <div className="flex items-center justify-between mt-1">
        <span className={`text-xs font-semibold ${
          risk.color === 'green' 
            ? 'text-green-600 dark:text-green-400' 
            : risk.color === 'yellow' 
            ? 'text-yellow-600 dark:text-yellow-400' 
            : 'text-red-600 dark:text-red-400'
        }`}>
          {risk.level}
        </span>
        <span className="text-xs text-gray-600 dark:text-gray-400">
          {value.toFixed(0)}/100
        </span>
      </div>
    </div>
  );
};

export default RiskMeter;

